/**
 * CodeFormatter utility for JavaScript/TypeScript code
 * Provides formatting capabilities for code in the browser
 */

// Type definition for Prettier if available in the window
interface PrettierInstance {
  format: (code: string, options: Record<string, unknown>) => string;
}

interface WindowWithPrettier extends Window {
  prettier?: PrettierInstance;
  prettierPlugins?: unknown[];
}

/**
 * Check if Prettier is available in the global scope
 */
const isPrettierAvailable = (): boolean => {
  return (
    !!(window as WindowWithPrettier).prettier &&
    !!(window as WindowWithPrettier).prettierPlugins
  );
};

/**
 * Format JavaScript/TypeScript code using Prettier if available
 * Falls back to a simpler formatter if Prettier is not available
 */
export const formatJavaScript = (code: string): string => {
  if (!code.trim()) return code;

  // Check for passenger function pattern which needs special handling
  // if (code.includes('paxReferences') && code.includes('paxType')) {
  //   return formatPassengerFunction(code);
  // }

  // Avoid reformatting already formatted code (with new lines)
  if (code.includes('\n') && !isPrettierAvailable()) return code;

  try {
    // Try to use Prettier if available
    if (isPrettierAvailable()) {
      const prettier = (window as WindowWithPrettier).prettier!;
      const prettierPlugins = (window as WindowWithPrettier).prettierPlugins!;

      return prettier.format(code, {
        parser: 'babel',
        plugins: prettierPlugins,
        printWidth: 80,
        tabWidth: 2,
        semi: true,
        singleQuote: true,
        trailingComma: 'es5',
        bracketSpacing: true,
      });
    }

    // Fall back to basic formatting if Prettier is not available
    return basicCodeFormat(code);
  } catch (error) {
    console.error('Error formatting code:', error);
    return basicCodeFormat(code);
  }
};

/**
 * Format JSON using Prettier if available
 * Falls back to JSON.stringify with indentation
 */
export const formatJSON = (json: string): string => {
  try {
    if (isPrettierAvailable()) {
      const prettier = (window as WindowWithPrettier).prettier!;
      const prettierPlugins = (window as WindowWithPrettier).prettierPlugins!;

      return prettier.format(json, {
        parser: 'json',
        plugins: prettierPlugins,
        printWidth: 80,
        tabWidth: 2,
      });
    }

    // Parse and stringify for basic formatting
    const parsed = JSON.parse(json);
    return JSON.stringify(parsed, null, 2);
  } catch (error) {
    console.error('Error formatting JSON:', error);
    return json;
  }
};

/**
 * Special formatter for passenger counting functions
 * This handles the specific pattern of the paxReferences function
 */
// const formatPassengerFunction = (code: string): string => {
//   try {
//     // Remove all whitespace to start with a clean slate
//     let formatted = code.replace(/\s+/g, ' ').trim();

//     // Part 1: Format the overall arrow function
//     formatted = formatted
//       .replace(/^\s*(\(.*?\))\s*=>\s*{/, '$1 => {')
//       .replace(/}\s*$/, '}');

//     // Part 2: Structure the entire function
//     formatted = `(input) => {
//   // Check if pax references exist
//   if (!input.fl3xx?.flight?.paxReferences || !Array.isArray(input.fl3xx?.flight?.paxReferences)) {
//     return {
//       male: 0,
//       female: 0,
//       children: 0,
//       infants: 0
//     };
//   }

//   // Extract passenger counts using reduce
//   const { male, female, children, infants } = input.fl3xx.flight.paxReferences.reduce(
//     {
//       male: 0,
//       female: 0,
//       children: 0,
//       infants: 0
//     },
//     (acc, cur) => {
//       // Count different passenger types
//       if (cur.paxType === 'MALE') {
//         acc.male++;
//       }
//       if (cur.paxType === 'FEMALE') {
//         acc.female++;
//       }
//       if (cur.paxType === 'CHILD') {
//         acc.children++;
//       }
//       if (cur.paxType === 'INFANT') {
//         acc.infants++;
//       }
//       return acc;
//     }
//   );

//   // Return the counts
//   return {
//     male,
//     female,
//     children,
//     infants
//   };
// }`;

//     return formatted;
//   } catch (error) {
//     console.error('Error in passenger function formatter:', error);
//     return basicCodeFormat(code);
//   }
// };

/**
 * Basic code formatter for JavaScript
 * Uses regex patterns to format the code
 */
const basicCodeFormat = (code: string): string => {
  // Normalize whitespace
  let formatted = code.replace(/\s+/g, ' ').trim();

  // Format arrow functions
  if (formatted.includes('=>')) {
    formatted = formatted
      .replace(/^\s*(\([^)]*\)|[^=>\s]+)\s*=>\s*{/, '$1 => {\n')
      .replace(/}\s*$/, '\n}');
  }

  // Format function declarations
  if (formatted.includes('function')) {
    formatted = formatted
      .replace(/function\s*\(\s*([^)]*)\s*\)\s*{/, 'function($1) {\n')
      .replace(/}\s*$/, '\n}');
  }

  // Format if statements
  formatted = formatted
    .replace(/if\s*\(([^{)]+)\)\s*{/g, 'if ($1) {\n')
    .replace(/}\s*else\s*{/g, '} else {\n');

  // Format object declarations
  formatted = formatted
    .replace(/{\s*([a-zA-Z0-9_]+):/g, '{\n  $1:')
    .replace(/,\s*([a-zA-Z0-9_]+):/g, ',\n  $1:');

  // Format return statements with objects
  formatted = formatted.replace(/return\s*{([^}]*)}/g, (match) => {
    return match
      .replace(/{\s*/, '{\n  ')
      .replace(/,\s*/g, ',\n  ')
      .replace(/}/, '\n}');
  });

  // Format statements with line breaks
  formatted = formatted
    .replace(/;\s*/g, ';\n')
    .replace(/{\s*/g, '{\n')
    .replace(/}\s*/g, '}\n');

  // Apply indentation
  const lines = formatted.split('\n');
  let indentLevel = 0;
  let result = '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Decrease indentation for closing brackets
    if (
      trimmed.startsWith('}') ||
      trimmed.startsWith(')') ||
      trimmed.startsWith(']')
    ) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    // Add indentation
    const indent = '  '.repeat(indentLevel);
    result += indent + trimmed + '\n';

    // Increase indentation for opening brackets
    if (
      trimmed.endsWith('{') ||
      trimmed.endsWith('(') ||
      trimmed.endsWith('[')
    ) {
      indentLevel++;
    }
  }

  // Clean up the formatted result
  return result
    .replace(/\n\s*\n+/g, '\n')
    .replace(/{\s*\n\s*}/g, '{ }')
    .replace(/\(\s*\n\s*\)/g, '()')
    .trim();
};

/**
 * Format code with specific language
 */
export const formatCode = (
  code: string,
  language: 'javascript' | 'typescript' | 'json',
): string => {
  if (!code.trim()) return code;

  if (language === 'json') {
    return formatJSON(code);
  }

  return formatJavaScript(code);
};

export default {
  formatJavaScript,
  formatJSON,
  formatCode,
  isPrettierAvailable,
};
